/**
 * WorkerCard — жив ли фоновый воркер и последний запуск каждой задачи.
 * Воркер молчит — триалы не закрываются, напоминания и пуши копятся.
 */

import {useQuery} from '@tanstack/react-query';
import {CircleAlert, CircleCheck} from 'lucide-react';

import {workerStatusQueryOptions} from '@services/system';
import {Loader} from '@components/ui/Loader';
import {cn} from '@lib/utils';

import {fmtDuration, fmtWhen, jobLabel, STATUS_META} from '../System.utils';

export const WorkerCard = () => {
  const {data, isLoading, isError} = useQuery(workerStatusQueryOptions());

  if (isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center rounded-[13px] border border-skr-line bg-card">
        <Loader label="Спрашиваем воркер…" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-[13px] border border-skr-line bg-card p-4 text-[13px] text-muted-foreground">
        Не удалось получить состояние воркера
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[13px] border border-skr-line bg-card">
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3.5',
          data.alive ? 'bg-[#EAF7EF]' : 'bg-[#FCEEEC]',
        )}
      >
        {data.alive ? (
          <CircleCheck className="size-5 text-skr-paid" strokeWidth={2} />
        ) : (
          <CircleAlert className="size-5 text-skr-danger" strokeWidth={2} />
        )}
        <div className="flex-1">
          <div
            className={cn(
              'text-sm font-semibold',
              data.alive ? 'text-[#1B6B41]' : 'text-[#A02E26]',
            )}
          >
            {data.alive ? 'Фоновые задачи работают' : 'Воркер не отвечает'}
          </div>
          <div className="text-[12.5px] text-skr-ink2">
            {data.alive
              ? `Последний отклик: ${fmtWhen(data.lastHeartbeatAt)}`
              : 'Триалы не закрываются, напоминания и пуши копятся в очереди. Нужен перезапуск воркера.'}
          </div>
        </div>
      </div>
      {data.lastRuns.map((r) => (
        <div
          key={r.id}
          className="flex items-center gap-3 border-t border-skr-hairline px-4 py-2.5"
        >
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold">{jobLabel(r.jobName)}</div>
            <div className="truncate text-[12px] text-muted-foreground">
              {fmtWhen(r.startedAt)} · {fmtDuration(r.durationMs)}
              {r.attempt > 1 ? ` · попытка ${r.attempt}` : ''}
              {r.detail !== null ? ` · ${r.detail}` : ''}
            </div>
          </div>
          <span
            className={cn(
              'shrink-0 rounded-full px-[9px] py-[3px] text-[11.5px] font-semibold',
              STATUS_META[r.status].className,
            )}
          >
            {STATUS_META[r.status].label}
          </span>
        </div>
      ))}
    </div>
  );
};
